import { Link } from 'react-router-dom'
import { AI4BA_BA_PROCESS_FLOWS } from '@/data/ai4baBaProcessFlows'
import { courseLink, findCoursesByCodes } from '@/config/careerPaths'
import type { PublicCourse } from '@/types/course'
import { Container, SectionHeading } from '@/components/ui'

interface EaseBaAiSectionProps {
  courses: PublicCourse[]
}

const STEP_HELPERS: Record<string, string> = {
  elicitation: 'AI hỗ trợ research, khơi gợi câu hỏi và tổng hợp nhanh insight từ stakeholder.',
  gap: 'AI đối chiếu AS-IS và TO-BE để làm rõ khoảng trống, phụ thuộc và ưu tiên triển khai.',
  overview: 'AI giúp mô hình hóa bức tranh tổng quan bằng use case, ERD và sơ đồ ngữ cảnh.',
  architecture: 'AI hỗ trợ chốt PRD, cấu trúc giải pháp và kết nối với mô hình dữ liệu triển khai.',
  'ui-design': 'AI tăng tốc IA, wireframe, UI spec và chuyển prototype thành đầu vào rõ ràng cho FE.',
  'us-spec': 'AI hỗ trợ viết user story theo loại, kèm flow xử lý và acceptance criteria nhất quán.',
  cr: 'AI truy vết tác động thay đổi, đồng bộ knowledge graph và giảm sót phạm vi CR.',
  coding: 'AI hỗ trợ sinh code từ đặc tả, kiểm tra chất lượng và tăng tốc đưa tính năng vào vận hành.',
}

const EaseBaAiSection = ({ courses }: EaseBaAiSectionProps) => {
  const ai4baCourse = findCoursesByCodes(courses, ['AI4BA'])[0] || null
  const processFlows = [
    ...AI4BA_BA_PROCESS_FLOWS.filter((flow) => flow.id !== 'master'),
    {
      id: 'coding',
      title: '8. Coding',
      subtitle: 'Chuyển đặc tả đã hoàn thiện thành tính năng có thể kiểm thử và triển khai.',
    },
  ]

  return (
    <section className="eh-ai-system eh-section" id="ai-productivity">
      <div className="eh-ai-system__rail" aria-hidden="true">
        <span>AI ACCELERATION LAYER</span>
      </div>
      <Container>
        <div className="eh-reveal relative z-[2] grid gap-10 lg:grid-cols-[1fr_1.4fr] lg:gap-[70px]">
          <div className="lg:sticky lg:top-7 lg:self-start">
            <SectionHeading
              eyebrow="Hệ thống tăng tốc công việc BA"
              level="title"
              title={
                <>
                  Tăng tốc toàn bộ quy trình BA với <em>AI</em>
                </>
              }
              description="Không chỉ hỏi AI theo cảm hứng. Mỗi phase BA đều có bộ skill và workflow chuyên biệt để làm nhanh hơn, nhất quán hơn và kiểm soát tốt đầu ra."
            />
            <div className="eh-ai-system__proof">
              <span><strong>08</strong> giai đoạn</span>
              <span><strong>01</strong> hệ Skill</span>
              <span><strong>∞</strong> khả năng tái sử dụng</span>
            </div>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/skills" className="eh-btn">
                Xem thư viện Skill <span className="eh-arrow">→</span>
              </Link>
              {ai4baCourse ? (
                <Link to={courseLink(ai4baCourse)} className="eh-btn-secondary">
                  Khóa AI4BA
                </Link>
              ) : (
                <Link to="/courses" className="eh-btn-secondary">
                  Xem khóa học
                </Link>
              )}
            </div>
          </div>

          <div className="eh-ai-timeline eh-stagger" role="list">
            {processFlows.map((flow, index) => (
              <article
                key={flow.id}
                role="listitem"
                className="eh-ai-timeline__item eh-reveal"
              >
                <div className="eh-display text-[clamp(2rem,3vw,2.75rem)] leading-none tracking-tight text-eh-forest-ink">
                  {String(index + 1).padStart(2, '0')}
                </div>
                <div>
                  <h3 className="eh-heading">
                    {flow.title.replace(/^\d+\.\s*/, '')}
                  </h3>
                  <p className="mt-2 text-sm leading-relaxed text-eh-muted">
                    {STEP_HELPERS[flow.id] || flow.subtitle}
                  </p>
                </div>
              </article>
            ))}
          </div>
        </div>
      </Container>
    </section>
  )
}

export default EaseBaAiSection
